import React, { useState, useEffect } from 'react';
import { requestFullscreen, exitFullscreen, isFullscreen } from '../utils/iframeDetection';
import '../styles/iframe.css';

/**
 * Fullscreen Button Component
 * Story 7.2 - Task 2: Fullscreen API implementation for constrained iframes
 *
 * Shows a fullscreen toggle button for users in constrained iframes
 * Requires user gesture to activate fullscreen mode
 */

interface FullscreenButtonProps {
  /** Only show button if this is true (typically when iframe is constrained) */
  show: boolean;
}

export function FullscreenButton({ show }: FullscreenButtonProps) {
  const [isInFullscreen, setIsInFullscreen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsInFullscreen(isFullscreen());
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('msfullscreenchange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('msfullscreenchange', handleFullscreenChange);
    };
  }, []);

  const handleToggleFullscreen = async () => {
    try {
      setError(null);

      if (isInFullscreen) {
        await exitFullscreen();
      } else {
        await requestFullscreen();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fullscreen not supported';
      setError(errorMessage);
      console.error('Fullscreen toggle failed:', err);

      // Clear error after 3 seconds
      setTimeout(() => setError(null), 3000);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <>
      <button
        className="fullscreen-button"
        onClick={handleToggleFullscreen}
        aria-label={isInFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
        title={isInFullscreen ? 'Exit fullscreen (Esc)' : 'Enter fullscreen'}
      >
        {isInFullscreen ? (
          // Exit fullscreen icon
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
          </svg>
        ) : (
          // Enter fullscreen icon
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3" />
          </svg>
        )}
        <span>{isInFullscreen ? 'Exit' : 'Fullscreen'}</span>
      </button>

      {error && (
        <div
          className="fullscreen-error"
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
        >
          {error}
        </div>
      )}
    </>
  );
}
